import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Briefcase, Building, Building2, Cable, CheckCircle2, ClipboardCheck, Droplets, Hammer, Home, ImagePlus, LayoutGrid, Loader2, Recycle, ShieldAlert, Sparkles, Wrench, X } from "lucide-react";
import { site, waLink, waMessages } from "@/content/siteContent";
import { scrollToSection } from "@/lib/smoothScroll";

const API = process.env.REACT_APP_BACKEND_URL;
const brl = (v) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const pct = (v) => `${(v * 100).toFixed(2).replace(".", ",")}%`;

const SERVICE_ICONS = { "andaime-janela": Building2, rapel: Cable, "usados-andaime": Wrench, "casa-nova": Home, empresa: Briefcase, "empresa-predio": Building, pmoc: ClipboardCheck, "rapel-usados": Recycle, "limpeza-rapel": Sparkles, "instalacao-geral": LayoutGrid, "instalacao-usados": Hammer, "higienizacao-geral": Droplets };

const minUnitOf = (s) => {
  if (!s) return 0;
  if (s.modalities) return 0; // resolvido no card com a lista de serviços
  if (s.floor_price_tiers) return Math.min(...s.floor_price_tiers.flat().map((t) => t[2]));
  if (s.brand_price_groups) return Math.min(...s.brand_price_groups.map((g) => (g.btu_prices ? Math.min(...Object.values(g.btu_prices)) : g.price)));
  if (s.btu_prices) return Math.min(...Object.values(s.btu_prices));
  return 0;
};

const cardTotal = (pix, fee) => Math.round((pix / (1 - fee) + 1e-9) * 100) / 100;
const splitInstallments = (total, n) => {
  const cents = Math.round(total * 100);
  const base = Math.floor(cents / n);
  const arr = Array(n).fill(base);
  arr[n - 1] += cents - base * n;
  return arr.map((a) => a / 100);
};

export default function HirePage() {
  const path = window.location.pathname;
  if (path.startsWith("/contratar/sucesso")) return <PaymentResult kind="sucesso" />;
  if (path.startsWith("/contratar/cancelado")) return <PaymentResult kind="cancelado" />;
  return <HireFlow />;
}

function PaymentResult({ kind }) {
  const [state, setState] = useState("verificando");
  useEffect(() => {
    if (kind !== "sucesso") return;
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) { setState("erro"); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/payments/status/${sid}`);
        if (data.payment_status === "pago") { setState("pago"); return; }
      } catch {}
      if (++tries < 10) setTimeout(poll, 2000);
      else setState("pendente");
    };
    poll();
  }, [kind]);

  return (
    <main className="honeycomb flex min-h-[70vh] items-center justify-center px-5 pt-24" data-testid="payment-result">
      <div className="card-industrial max-w-lg p-10 text-center">
        {kind === "cancelado" ? (
          <>
            <ShieldAlert className="mx-auto h-12 w-12 text-brand" />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-paper">Pagamento não concluído</h1>
            <p className="mt-3 text-sm text-mist">Nenhuma cobrança foi feita. Se precisar, fale com a nossa equipe.</p>
          </>
        ) : state === "pago" ? (
          <>
            <CheckCircle2 className="mx-auto h-12 w-12 text-brand" />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-paper">Pagamento confirmado</h1>
            <p className="mt-3 text-sm text-mist">Recebemos o seu pagamento. Nossa equipe entrará em contato para os próximos passos da instalação.</p>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand" />
            <h1 className="mt-5 font-display text-2xl font-extrabold text-paper">Confirmando pagamento...</h1>
            <p className="mt-3 text-sm text-mist">Aguarde enquanto confirmamos com o provedor de pagamento.</p>
          </>
        )}
        <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-industrial mt-8 w-full" data-testid="payment-result-whatsapp">
          FALAR COM A EQUIPE
        </a>
      </div>
    </main>
  );
}

function HireFlow() {
  const [services, setServices] = useState([]);
  const [config, setConfig] = useState(null);
  const [service, setService] = useState(null);
  const [form, setForm] = useState({ nome: "", whatsapp: "", endereco: "", empresa: "", maquinas_tipo: "exata", maquinas: "", pontos: "", modalidade: "", quantidade: "1", devices: [{ btu: "", brand: "" }], andar: "", complemento: "" });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [method, setMethod] = useState("pix");
  const [installments, setInstallments] = useState(1);
  const [accepted, setAccepted] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [invalid, setInvalid] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/hire/services`).then((r) => setServices(r.data)).catch(() => {});
    axios.get(`${API}/api/hire/config`).then((r) => setConfig(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  // Serviço agregador ("Instalação Geral"): a modalidade escolhida aponta para o serviço que define opções e valores
  const eff = useMemo(() => {
    if (!service?.modalities) return service;
    const ref = service.modalities.find((m) => m.id === form.modalidade)?.service_ref;
    return ref ? services.find((x) => x.id === ref) || null : null;
  }, [service, services, form.modalidade]);

  const deviceUnit = (s, d) => {
    if (!d.btu) return 0;
    if (s.floor_price_tiers) {
      const idx = s.btu_tier_map?.[d.btu];
      const n = parseInt(form.andar, 10);
      if (idx === undefined || Number.isNaN(n)) return 0;
      const tier = s.floor_price_tiers[idx].find(([mn, mx]) => n >= mn && n <= mx);
      return tier ? tier[2] : 0;
    }
    if (s.brand_price_groups) {
      const g = s.brand_price_groups.find((gr) => gr.brands.includes(d.brand));
      if (!g) return 0;
      return g.btu_prices ? (g.btu_prices[d.btu] ?? 0) : g.price;
    }
    if (s.btu_prices) {
      if (s.premium_btu_prices && s.premium_brands?.includes(d.brand) && s.premium_btu_prices[d.btu] != null) return s.premium_btu_prices[d.btu];
      return s.btu_prices[d.btu] ?? 0;
    }
    return 0;
  };

  const pixPrice = useMemo(() => {
    if (!service || !eff) return 0;
    if (eff.floor_rule && (!form.andar || form.andar === "outro")) return 0;
    if (!form.devices.length || form.devices.some((d) => !d.btu || d.btu === "sob-consulta" || (eff.brand_mode && !d.brand))) return 0;
    return form.devices.reduce((sum, d) => sum + deviceUnit(eff, d), 0);
  }, [service, eff, form]);

  const pricing = useMemo(() => {
    if (!pixPrice || !config) return null;
    if (method === "pix") return { fee: 0, total: pixPrice, values: [pixPrice] };
    if (method === "debito") {
      const total = cardTotal(pixPrice, config.card_fees.debito);
      return { fee: config.card_fees.debito, total, values: [total] };
    }
    const fee = config.card_fees.credito[String(installments)];
    const total = cardTotal(pixPrice, fee);
    return { fee, total, values: splitInstallments(total, installments) };
  }, [pixPrice, method, installments, config]);

  const contactFilled = Boolean(form.nome.trim()) && form.whatsapp.replace(/\D/g, "").length >= 10 && (service?.quote_only ? Boolean(form.empresa.trim()) : Boolean(form.endereco.trim()));

  const pendingMsg = (() => {
    if (!service) return "";
    if (!contactFilled) return "Preencha nome, WhatsApp e condomínio ou endereço na etapa 02 para liberar as opções e ver o valor.";
    const parts = [service.modalities && "a modalidade", "a quantidade", "os BTUs de cada aparelho", eff?.brand_mode && "a marca de cada aparelho", eff?.floor_rule && "o andar"].filter(Boolean);
    return `Selecione ${parts.slice(0, -1).join(", ")}${parts.length > 1 ? " e " : ""}${parts[parts.length - 1]} na etapa anterior para ver o valor.`;
  })();

  // Captura automática de lead: assim que os dados de contato ficam completos, envia para a empresa
  const leadSentRef = useRef("");
  useEffect(() => {
    if (!service || !contactFilled) return;
    const modLabel = service.modalities?.find((m) => m.id === form.modalidade)?.label || "";
    const key = `${service.id}|${form.nome}|${form.whatsapp}|${form.endereco}|${form.empresa}|${modLabel}`;
    if (leadSentRef.current === key) return;
    const t = setTimeout(() => {
      leadSentRef.current = key;
      axios.post(`${API}/api/hire/leads`, {
        nome: form.nome,
        whatsapp: form.whatsapp,
        endereco: form.endereco || "",
        empresa: form.empresa || null,
        service_title: service.title,
        modalidade_label: modLabel || null,
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [contactFilled, form.nome, form.whatsapp, form.endereco, form.empresa, form.modalidade, service]);

  const sobConsulta = useMemo(
    () => form.quantidade === "mais5" || form.andar === "outro" || form.devices.some((d) => d.btu === "sob-consulta"),
    [form.quantidade, form.andar, form.devices]
  );

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setInvalid("");
    setError("");
  };

  const setQuantidade = (e) => {
    const v = e.target.value;
    setForm((f) => {
      if (v === "mais5") return { ...f, quantidade: v };
      const n = parseInt(v, 10);
      return { ...f, quantidade: v, devices: Array.from({ length: n }, (_, i) => f.devices[i] || { btu: "", brand: "" }) };
    });
    setInvalid("");
    setError("");
  };

  const setModalidade = (e) => {
    setForm((f) => ({ ...f, modalidade: e.target.value, quantidade: "1", devices: [{ btu: "", brand: "" }], andar: "" }));
    setInvalid("");
    setError("");
  };

  const setDevice = (i, key) => (e) => {
    setForm((f) => ({ ...f, devices: f.devices.map((d, j) => (j === i ? { ...d, [key]: e.target.value } : d)) }));
    setInvalid("");
    setError("");
  };

  const fc = (key) => `field-industrial${invalid === key ? " !border-brand !shadow-[0_0_0_3px_rgba(245,176,0,0.15)]" : ""}`;

  const focusField = (testid) => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setTimeout(() => el.focus({ preventScroll: true }), 450);
    }
  };

  const validate = () => {
    const checks = [
      ["nome", "hire-input-nome", !form.nome.trim(), "Preencha o campo: Nome completo"],
      ["whatsapp", "hire-input-whatsapp", form.whatsapp.replace(/\D/g, "").length < 10, "Informe um WhatsApp válido com DDD"],
    ];
    if (service.quote_only) {
      checks.push(["empresa", "hire-input-empresa", !form.empresa.trim(), "Preencha o campo: Nome da empresa"]);
      checks.push(["maquinas", "hire-input-maquinas", !form.maquinas || parseInt(form.maquinas, 10) < 1, "Informe a quantidade de máquinas"]);
      checks.push(["pontos", "hire-input-pontos", !form.pontos || parseInt(form.pontos, 10) < 1, "Informe a quantidade de pontos / empresas"]);
    } else {
      checks.push(["endereco", "hire-input-endereco", !form.endereco.trim(), "Preencha o campo: Condomínio ou endereço"]);
      if (service.modalities) checks.push(["modalidade", "hire-select-modalidade", !form.modalidade, "Selecione a modalidade da instalação"]);
      checks.push(["quantidade", "hire-select-quantidade", !form.quantidade || form.quantidade === "mais5", "Selecione quantos aparelhos pretende instalar"]);
      form.devices.forEach((d, i) => {
        checks.push([`btu-${i}`, `hire-select-btu-${i}`, !d.btu, `Selecione os BTUs do aparelho ${i + 1}`]);
        if (eff?.brand_mode) checks.push([`marca-${i}`, `hire-select-marca-${i}`, !d.brand, `Selecione a marca do aparelho ${i + 1}`]);
      });
      if (eff?.floor_rule) checks.push(["andar", "hire-select-andar", !form.andar || form.andar === "outro", "Selecione o andar do apartamento"]);
    }
    checks.push(["accept", "hire-accept-checkbox", !accepted, "Marque a confirmação das condições para continuar"]);
    const fail = checks.find((c) => c[2]);
    if (fail) {
      setInvalid(fail[0]);
      setError(fail[3]);
      focusField(fail[1]);
      return false;
    }
    return true;
  };

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError("");
    try {
      const data = new FormData();
      files.slice(0, 5 - photos.length).forEach((f) => data.append("files", f));
      const { data: res } = await axios.post(`${API}/api/hire/upload`, data);
      setPhotos((p) => [...p, ...res.files]);
    } catch (err) {
      setError(err.response?.data?.detail || "Falha ao enviar anexo");
    }
    setUploading(false);
  };

  const submit = async () => {
    if (sending || sobConsulta) return;
    if (!validate()) return;
    setSending(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/hire/orders`, {
        service_id: service.id,
        modalidade: form.modalidade || null,
        andar: form.andar && form.andar !== "outro" ? parseInt(form.andar, 10) : null,
        devices: service.quote_only ? [] : form.devices.map((d) => ({ btu: d.btu, brand: d.brand || null })),
        nome: form.nome,
        whatsapp: form.whatsapp,
        endereco: form.endereco,
        empresa: form.empresa || null,
        maquinas: form.maquinas ? parseInt(form.maquinas, 10) : null,
        maquinas_tipo: form.maquinas_tipo,
        pontos: form.pontos ? parseInt(form.pontos, 10) : null,
        complemento: form.complemento,
        photos: photos.map((p) => p.path),
        method: service.quote_only ? null : method,
        installments: method === "credito" ? installments : 1,
      });
      setDone(data);
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.response?.data?.detail || "Não foi possível registrar o pedido");
    }
    setSending(false);
  };

  if (done) {
    return (
      <main className="honeycomb flex min-h-[80vh] items-center justify-center px-5 py-28" data-testid="hire-success">
        <div className="card-industrial max-w-xl p-10 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-brand" />
          <h1 className="mt-6 font-display text-3xl font-extrabold text-paper">Pedido recebido!</h1>
          <p className="mt-4 text-sm leading-relaxed text-mist">
            Pedido <span className="font-bold text-brand">#{done.id.slice(0, 8).toUpperCase()}</span> registrado.
            {done.total != null
              ? " Nossa equipe vai analisar a viabilidade técnica do local e enviar o link de pagamento no seu WhatsApp. A instalação ainda não está agendada."
              : " Nossa equipe vai analisar as informações e enviar a proposta no seu WhatsApp."}
          </p>
          <div className="mt-6 rounded-2xl border border-metal bg-ink p-5 text-left text-sm text-mist">
            <p><span className="text-steel">Serviço:</span> <span className="font-semibold text-paper">{done.service_title}</span></p>
            <p className="mt-1"><span className="text-steel">Aparelhos:</span> <span className="font-semibold text-paper">{done.capacidade}</span></p>
            {done.andar && <p className="mt-1"><span className="text-steel">Andar:</span> <span className="font-semibold text-paper">{done.andar}º</span></p>}
            {done.total != null ? (
              <p className="mt-1"><span className="text-steel">Total:</span> <span className="font-semibold text-brand">{brl(done.total)}</span>
                {done.installments > 1 && <span> em {done.installments}x de {brl(done.installment_values[0])}</span>}
              </p>
            ) : (
              <p className="mt-1"><span className="text-steel">Total:</span> <span className="font-semibold text-brand">Sob consulta</span></p>
            )}
          </div>
          <a
            href={`https://wa.me/5565996091184?text=${encodeURIComponent([
              `Novo pedido #${done.id.slice(0, 8).toUpperCase()}`,
              `Serviço: ${done.service_title}`,
              done.brand && `Marca: ${done.brand}`,
              done.andar && `Andar: ${done.andar}º`,
              `Capacidade: ${done.capacidade}`,
              done.method
                ? `Pagamento: ${done.method === "pix" ? "Pix" : done.method === "debito" ? "Débito" : `Crédito ${done.installments}x`} — Total ${brl(done.total)}${done.installments > 1 ? ` (${done.installments}x de ${brl(done.installment_values[0])})` : ""}`
                : "Valores e planos: após análise técnica",
              `Nome: ${done.nome}`,
              `WhatsApp: ${done.whatsapp}`,
              done.empresa && `Empresa: ${done.empresa}`,
              `Endereço: ${done.endereco}`,
              done.complemento && `Complemento: ${done.complemento}`,
              done.photos?.length ? `Fotos/vídeos do local: ${done.photos.length} enviado(s) no pedido` : null,
            ].filter(Boolean).join("\n"))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-industrial mt-8 w-full"
            data-testid="hire-success-whatsapp"
          >
            ENVIAR RESUMO NO WHATSAPP DA SAGARELO
          </a>
          <a href={waLink(waMessages.instalacao)} target="_blank" rel="noopener noreferrer" className="btn-outline-industrial mt-3 w-full" data-testid="hire-success-talk">
            FALAR COM A EQUIPE
          </a>
        </div>
      </main>
    );
  }

  return (
    <main className="honeycomb bg-ink px-5 pb-24 pt-28 md:px-8" data-testid="hire-page">
      <div className="relative z-10 mx-auto max-w-6xl">
        <div className="mb-10">
          <div className="microtitle-row mb-4">
            <p className="microtitle">CONTRATAÇÃO ONLINE</p>
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-paper sm:text-5xl">
            CONTRATE SUA <span className="text-brand">INSTALAÇÃO.</span>
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-mist md:text-base">
            Serviços de instalação de ar-condicionado (não inclui a venda do aparelho). Escolha o serviço, informe os dados e a forma de pagamento.
          </p>
        </div>

        {/* PASSO 1 — SERVIÇOS */}
        <p className="microtitle mb-5">01 — ESCOLHA O SERVIÇO</p>
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="hire-services">
          {services.filter((s) => s.active !== false).map((s, idx) => {
            const Icon = SERVICE_ICONS[s.id] || Building2;
            const minPrice = s.quote_only
              ? 0
              : s.modalities
                ? Math.min(...s.modalities.map((m) => minUnitOf(services.find((x) => x.id === m.service_ref))))
                : minUnitOf(s);
            const selected = service?.id === s.id;
            return (
              <article key={s.id} className={`card-industrial flex flex-col overflow-hidden ${selected ? "!border-brand" : ""}`} data-testid={`hire-service-${s.id}`}>
                <div className="relative h-44 overflow-hidden bg-coal">
                  {s.image_url ? (
                    <img src={`${API}${s.image_url}`} alt={s.title} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <span className="medallion" style={{ width: 72, height: 72 }}>
                        <span className="medallion-inner"><Icon className="h-8 w-8 text-brand" strokeWidth={1.6} /></span>
                      </span>
                    </div>
                  )}
                  <span className="absolute left-3 top-3 z-10 rounded-md border border-brand/50 bg-ink/85 px-2 py-0.5 font-display text-[0.65rem] font-extrabold tracking-widest text-brand" data-testid={`hire-num-${s.id}`}>{String(idx + 1).padStart(2, "0")}</span>
                  <div className="absolute inset-0 bg-gradient-to-t from-graphite via-transparent to-transparent" />
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-display text-base font-extrabold leading-snug text-paper">{s.title}</h3>
                  {s.tagline && (
                    <p className="mt-1 text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-steel" data-testid={`hire-tagline-${s.id}`}>{s.tagline}</p>
                  )}
                  {s.tagline2 && (
                    <p className="mt-0.5 text-[0.7rem] font-bold uppercase tracking-[0.14em] text-brand" data-testid={`hire-tagline2-${s.id}`}>{s.tagline2}</p>
                  )}
                  <p className="mt-1 text-[0.7rem] font-bold tracking-[0.14em] text-brand">{s.btu}</p>
                  <p className="mt-3 flex-1 text-xs leading-relaxed text-mist">{s.description}</p>
                  {s.observation && (
                    <p className="mt-3 rounded-xl border border-brand/40 bg-ink p-3 text-[0.68rem] leading-relaxed text-mist" data-testid={`hire-obs-${s.id}`}>
                      {s.observation}
                    </p>
                  )}
                  {s.quote_only ? (
                    <p className="mt-4 font-display text-base font-extrabold leading-snug text-brand" data-testid={`hire-quote-${s.id}`}>Valores e planos após análise técnica</p>
                  ) : (
                    <p className="mt-4 font-display text-xl font-extrabold text-paper">
                      <span className="text-xs font-bold text-steel">A partir de </span>
                      {brl(minPrice)} <span className="text-xs font-bold text-brand">no Pix</span>
                    </p>
                  )}
                  {!s.quote_only && (
                    <p className="mt-2 text-[0.68rem] italic leading-relaxed text-mist" data-testid={`hire-hint-${s.id}`}>Selecione o serviço, preencha os dados e veja o valor real.</p>
                  )}
                  {s.price_note && (
                    <p className="mt-2 text-[0.68rem] italic leading-relaxed text-steel" data-testid={`hire-note-${s.id}`}>{s.price_note}</p>
                  )}
                  <button
                    onClick={() => { setService(s); setForm((f) => ({ ...f, modalidade: "", quantidade: "1", devices: [{ btu: "", brand: "" }], andar: "" })); setError(""); setInvalid(""); scrollToSection("#hire-step-2", -90); setTimeout(() => scrollToSection("#hire-step-2", -90), 650); setTimeout(() => document.querySelector('[data-testid="hire-input-nome"]')?.focus({ preventScroll: true }), 1200); }}
                    className={selected ? "btn-industrial mt-5 w-full" : "btn-outline-industrial mt-5 w-full"}
                    data-testid={`hire-select-${s.id}`}
                  >
                    {selected ? "SELECIONADO" : "FAZER COTAÇÃO ON-LINE"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>

        {service && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* PASSO 2 — DADOS */}
            <div id="hire-step-2" className="mt-14">
              <p className="microtitle mb-5">02 — SEUS DADOS E O LOCAL</p>
              <div className="card-industrial p-6 md:p-8" data-testid="hire-form">
                <div className="grid gap-4 md:grid-cols-2">
                  <input className={fc("nome")} placeholder="Nome completo*" value={form.nome} onChange={set("nome")} data-testid="hire-input-nome" />
                  <input className={fc("whatsapp")} placeholder="WhatsApp com DDD*" value={form.whatsapp} onChange={set("whatsapp")} data-testid="hire-input-whatsapp" />
                  {service.quote_only ? (
                    <input className={`${fc("empresa")} md:col-span-2`} placeholder="Nome da empresa*" value={form.empresa} onChange={set("empresa")} data-testid="hire-input-empresa" />
                  ) : (
                    <input className={`${fc("endereco")} md:col-span-2`} placeholder="Condomínio ou endereço*" value={form.endereco} onChange={set("endereco")} data-testid="hire-input-endereco" />
                  )}
                  {!contactFilled ? (
                    <p className="rounded-xl border border-brand/40 bg-ink p-4 text-xs font-semibold text-brand md:col-span-2" data-testid="hire-contact-gate">
                      {service.quote_only
                        ? "Preencha nome, WhatsApp e nome da empresa acima para continuar."
                        : "Preencha nome, WhatsApp e condomínio ou endereço acima para liberar a quantidade de aparelhos."}
                    </p>
                  ) : service.quote_only ? (
                    <>
                      <select className={fc("maquinas_tipo")} value={form.maquinas_tipo} onChange={set("maquinas_tipo")} data-testid="hire-select-maquinas-tipo">
                        <option value="exata">Quantidade exata de máquinas</option>
                        <option value="estimada">Quantidade média / estimada de máquinas</option>
                      </select>
                      <input className={fc("maquinas")} type="number" min="1" placeholder="Quantidade de máquinas*" value={form.maquinas} onChange={set("maquinas")} data-testid="hire-input-maquinas" />
                      <input className={`${fc("pontos")} md:col-span-2`} type="number" min="1" placeholder="Quantidade de pontos / empresas*" value={form.pontos} onChange={set("pontos")} data-testid="hire-input-pontos" />
                    </>
                  ) : (
                    <>
                      {service.modalities && (
                        <select className={`${fc("modalidade")} md:col-span-2`} value={form.modalidade} onChange={setModalidade} data-testid="hire-select-modalidade">
                          <option value="" disabled>Apartamento Andaime, Rapel, Empresa ou Casa?*</option>
                          {service.modalities.map((m) => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                      )}
                      {service.modality_notes?.[form.modalidade] && (
                        <p className="text-[0.72rem] font-semibold italic leading-relaxed text-brand md:col-span-2" data-testid="hire-modalidade-note">{service.modality_notes[form.modalidade]}</p>
                      )}
                      {(!service.modalities || form.modalidade) && eff && (
                        <>
                      <select className={`${fc("quantidade")} md:col-span-2`} value={form.quantidade} onChange={setQuantidade} data-testid="hire-select-quantidade">
                        <option value="" disabled>Quantos aparelhos pretende instalar?*</option>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={String(n)}>{n} {n === 1 ? "aparelho" : "aparelhos"}</option>
                        ))}
                        <option value="mais5">Mais de 5 aparelhos — sob consulta</option>
                      </select>
                      {form.quantidade !== "mais5" && form.devices.map((d, i) => (
                        <div key={i} className={`grid gap-4 ${eff.brand_mode ? "md:col-span-2 md:grid-cols-2" : ""}`}>
                          <select className={fc(`btu-${i}`)} value={d.btu} onChange={setDevice(i, "btu")} data-testid={`hire-select-btu-${i}`}>
                            <option value="" disabled>BTUs do aparelho {i + 1}*</option>
                            {eff.btu_options.map((b) => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                            {eff.btu_sob_consulta && <option value="sob-consulta">Outra capacidade — falar com atendente</option>}
                          </select>
                          {eff.brand_mode && (
                            <select className={fc(`marca-${i}`)} value={d.brand} onChange={setDevice(i, "brand")} data-testid={`hire-select-marca-${i}`}>
                              <option value="" disabled>Marca do aparelho {i + 1}*</option>
                              {eff.brand_options.map((b) => (
                                <option key={b} value={b}>{b}</option>
                              ))}
                            </select>
                          )}
                          {eff.btu_notes?.[d.btu] && (
                            <p className="text-[0.68rem] italic leading-relaxed text-brand md:col-span-2" data-testid={`hire-btu-note-${i}`}>{eff.btu_notes[d.btu]}</p>
                          )}
                        </div>
                      ))}
                      {eff.floor_rule && (
                        <select className={fc("andar")} value={form.andar} onChange={set("andar")} data-testid="hire-select-andar">
                          <option value="" disabled>Andar do apartamento*</option>
                          {Array.from({ length: eff.floor_rule.max - eff.floor_rule.min + 1 }, (_, i) => eff.floor_rule.min + i).map((n) => (
                            <option key={n} value={n}>{n}º andar</option>
                          ))}
                          <option value="outro">Outro andar — sob consulta</option>
                        </select>
                      )}
                        </>
                      )}
                    </>
                  )}
                  <textarea className="field-industrial resize-none md:col-span-2" rows={3} placeholder="Informações complementares (opcional)" value={form.complemento} onChange={set("complemento")} data-testid="hire-input-complemento" />
                </div>

                <div className="mt-5">
                  <label className="btn-outline-industrial cursor-pointer !px-5 !py-3" data-testid="hire-upload-button">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
                    ENVIAR FOTOS OU VÍDEOS DO LOCAL ({photos.length}/5)
                    <input type="file" accept="image/*,video/*,.heic,.heif" multiple className="hidden" onChange={handlePhotos} disabled={uploading || photos.length >= 5} />
                  </label>
                  {photos.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-3">
                      {photos.map((p) => (
                        <div key={p.path} className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-metal">
                          {/\.(mp4|mov|webm)$/i.test(p.path) ? (
                            <video src={`${API}${p.url}`} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                          ) : (
                            <img src={`${API}${p.url}`} alt="Anexo do local" className="h-full w-full object-cover" />
                          )}
                          <button onClick={() => setPhotos((arr) => arr.filter((x) => x.path !== p.path))} className="absolute right-1 top-1 rounded-full bg-ink/80 p-1 text-brand" aria-label="Remover anexo" data-testid="hire-photo-remove">
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* PASSO 3 — PAGAMENTO / SOB CONSULTA */}
            {sobConsulta ? (
              <div className="mt-14">
                <p className="microtitle mb-5">03 — SOB CONSULTA</p>
                <div className="card-industrial p-6 text-center md:p-10" data-testid="hire-sob-consulta">
                  <ShieldAlert className="mx-auto h-10 w-10 text-brand" />
                  <h3 className="mt-4 font-display text-xl font-extrabold text-paper">Instalação sob consulta</h3>
                  <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-mist">
                    Para mais de 5 aparelhos ou outro andar, o orçamento é personalizado. Fale direto com a nossa equipe pelo WhatsApp ou por e-mail e informe os dados dos aparelhos e do local.
                  </p>
                  <div className="mx-auto mt-6 grid max-w-lg gap-3 sm:grid-cols-2">
                    <a href={waLink(waMessages.rapel)} target="_blank" rel="noopener noreferrer" className="btn-industrial" data-testid="hire-consulta-whatsapp">FALAR NO WHATSAPP</a>
                    <a href={`mailto:${site.email}?subject=${encodeURIComponent(`Orçamento sob consulta — ${service.title}`)}`} className="btn-outline-industrial" data-testid="hire-consulta-email">ENVIAR E-MAIL</a>
                  </div>
                </div>
              </div>
            ) : (
            <div className="mt-14">
              <p className="microtitle mb-5">03 — FORMA DE PAGAMENTO</p>
              <div className="grid gap-5 lg:grid-cols-[1.2fr_1fr]">
                <div className="card-industrial p-6 md:p-8" data-testid="hire-payment">
                  {service.quote_only ? (
                    <div className="rounded-xl border border-brand/40 bg-ink p-4" data-testid="hire-quote-note">
                      <p className="font-display text-[0.68rem] font-extrabold tracking-[0.16em] text-brand">ORÇAMENTO PERSONALIZADO</p>
                      <p className="mt-2 text-xs leading-relaxed text-mist">Valores e planos após análise técnica: nossa equipe avalia a quantidade de máquinas e de pontos/empresas e envia a proposta no seu WhatsApp.</p>
                    </div>
                  ) : (
                  <>
                  <div className="grid grid-cols-3 gap-2.5">
                    {[["pix", "PIX"], ["debito", "DÉBITO"], ["credito", "CRÉDITO ATÉ 21x"]].map(([key, label]) => (
                      <button
                        key={key}
                        onClick={() => setMethod(key)}
                        className={`rounded-xl border px-3 py-3.5 font-display text-[0.7rem] font-extrabold tracking-[0.12em] transition-[background-color,border-color,color] duration-300 ${method === key ? "border-brand bg-brand text-ink" : "border-metal bg-ink text-mist hover:border-brand/60"}`}
                        data-testid={`hire-method-${key}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>

                  {method === "pix" && (
                    <div className="mt-4 rounded-xl border border-metal bg-ink p-4">
                      <p className="font-display text-[0.68rem] font-extrabold tracking-[0.16em] text-brand">PAGAMENTO VIA PIX</p>
                      <p className="mt-2 text-xs leading-relaxed text-mist" data-testid="hire-pix-info">
                        Os dados completos serão enviados no seu WhatsApp após a aprovação da viabilidade.
                      </p>
                    </div>
                  )}

                  {method === "credito" && config && pixPrice > 0 && (
                    <select className="field-industrial mt-4" value={installments} onChange={(e) => setInstallments(parseInt(e.target.value, 10))} data-testid="hire-select-installments">
                      {Array.from({ length: 21 }, (_, i) => i + 1).map((n) => {
                        const fee = config.card_fees.credito[String(n)];
                        const total = cardTotal(pixPrice, fee);
                        const values = splitInstallments(total, n);
                        return (
                          <option key={n} value={n}>
                            {n === 1 ? "À vista" : `${n}x`} — {n}x de {brl(values[0])} (total {brl(total)})
                          </option>
                        );
                      })}
                    </select>
                  )}

                  {!pixPrice && !sobConsulta && (
                    <p className="mt-4 rounded-xl border border-brand/40 bg-ink p-4 text-xs font-semibold text-brand" data-testid="hire-price-pending">
                      {pendingMsg}
                    </p>
                  )}
                  </>
                  )}

                  <div className="mt-6 flex items-start gap-3 rounded-xl border border-brand/40 bg-ink p-4">
                    <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand" />
                    <p className="text-xs leading-relaxed text-mist" data-testid="hire-disclaimer">{config?.disclaimer}</p>
                  </div>

                  {config?.policy?.configured && (
                    <div className="mt-4 rounded-xl border border-metal bg-ink p-4">
                      <p className="font-display text-[0.68rem] font-extrabold tracking-[0.16em] text-brand">POLÍTICA EM CASO DE INVIABILIDADE</p>
                      <p className="mt-2 text-xs leading-relaxed text-mist" data-testid="hire-policy-text">{config.policy.text}</p>
                    </div>
                  )}

                  <label className="mt-5 flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-mist">
                    <input type="checkbox" checked={accepted} onChange={(e) => { setAccepted(e.target.checked); setInvalid(""); setError(""); }} className={`mt-0.5 h-4 w-4 accent-[#F5B000] ${invalid === "accept" ? "outline outline-2 outline-brand" : ""}`} data-testid="hire-accept-checkbox" />
                    {service.quote_only
                      ? "Li e estou ciente das condições acima. Entendo que os valores e planos serão enviados após a análise técnica pela equipe."
                      : "Li e estou ciente das condições acima. Entendo que o pagamento só será cobrado após a confirmação da viabilidade pela equipe."}
                  </label>
                </div>

                {/* RESUMO */}
                <div className="card-industrial h-fit p-6 md:p-8" data-testid="hire-summary">
                  <p className="font-display text-[0.7rem] font-extrabold tracking-[0.18em] text-brand">RESUMO DO PEDIDO</p>
                  <dl className="mt-5 space-y-3 text-sm">
                    <div className="flex justify-between gap-4"><dt className="text-steel">Serviço</dt><dd className="text-right font-semibold text-paper">{service.title}</dd></div>
                    {form.andar && form.andar !== "outro" && <div className="flex justify-between gap-4"><dt className="text-steel">Andar</dt><dd className="font-semibold text-paper">{form.andar}º</dd></div>}
                    {service.quote_only ? (
                      <>
                        <div className="flex justify-between gap-4"><dt className="text-steel">Empresa</dt><dd className="text-right font-semibold text-paper">{form.empresa || "—"}</dd></div>
                        <div className="flex justify-between gap-4"><dt className="text-steel">Máquinas</dt><dd className="text-right font-semibold text-paper">{form.maquinas ? `${form.maquinas} (${form.maquinas_tipo === "exata" ? "quantidade exata" : "quantidade média/estimada"})` : "—"}</dd></div>
                        <div className="flex justify-between gap-4"><dt className="text-steel">Pontos / empresas</dt><dd className="text-right font-semibold text-paper">{form.pontos || "—"}</dd></div>
                        <div className="border-t border-metal pt-3" />
                        <div className="flex items-baseline justify-between gap-4">
                          <dt className="text-steel">Investimento</dt>
                          <dd className="font-display text-lg font-extrabold text-brand" data-testid="hire-total">Sob consulta</dd>
                        </div>
                      </>
                    ) : (
                      <>
                    {form.modalidade && service.modalities && <div className="flex justify-between gap-4"><dt className="text-steel">Modalidade</dt><dd className="text-right font-semibold text-paper">{service.modalities.find((m) => m.id === form.modalidade)?.label}</dd></div>}
                    <div className="flex justify-between gap-4"><dt className="text-steel">Aparelhos</dt><dd className="text-right font-semibold text-paper">{form.devices.length && form.devices.every((d) => d.btu && (!eff?.brand_mode || d.brand)) ? form.devices.map((d) => `${d.btu}${d.brand ? ` ${d.brand}` : ""}`).join(" + ") : "—"}</dd></div>
                    <div className="border-t border-metal pt-3" />
                    <div className="flex justify-between gap-4"><dt className="text-steel">Preço no Pix</dt><dd className="font-semibold text-paper">{pixPrice ? brl(pixPrice) : "—"}</dd></div>
                    <div className="flex justify-between gap-4"><dt className="text-steel">Forma</dt><dd className="font-semibold text-paper">{method === "pix" ? "Pix" : method === "debito" ? "Cartão de débito" : `Crédito ${installments}x`}</dd></div>
                    {pricing && method !== "pix" && (
                      <div className="flex justify-between gap-4"><dt className="text-steel">Taxa da operadora</dt><dd className="font-semibold text-brand">{pct(pricing.fee)}</dd></div>
                    )}
                    <div className="border-t border-metal pt-3" />
                    <div className="flex items-baseline justify-between gap-4">
                      <dt className="text-steel">Total</dt>
                      <dd className="font-display text-2xl font-extrabold text-brand" data-testid="hire-total">{pricing ? brl(pricing.total) : "—"}</dd>
                    </div>
                    {pricing && method === "credito" && installments > 1 && (
                      <p className="text-right text-xs text-mist" data-testid="hire-installment-info">
                        {installments}x de {brl(pricing.values[0])}
                        {pricing.values[installments - 1] !== pricing.values[0] && ` (última ${brl(pricing.values[installments - 1])})`}
                      </p>
                    )}
                      </>
                    )}
                  </dl>
                  {error && <p className="mt-4 rounded-xl border border-brand/40 bg-ink p-3 text-xs font-semibold text-brand" data-testid="hire-error">{error}</p>}
                  <button onClick={submit} disabled={sending} className="btn-industrial mt-6 w-full disabled:opacity-50" data-testid="hire-submit-button">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <BadgeCheck className="h-4 w-4" />}
                    {sending ? "ENVIANDO..." : service.quote_only ? "SOLICITAR ORÇAMENTO PMOC" : "ENVIAR PEDIDO"}
                  </button>
                  <p className="mt-3 text-center text-[0.68rem] text-steel">{service.quote_only ? "A proposta é enviada após a análise técnica" : "A cobrança é enviada após a análise de viabilidade"}</p>
                </div>
              </div>
            </div>
            )}
          </motion.div>
        )}

        <a href="/" className="mt-14 inline-flex items-center gap-2 text-xs font-bold tracking-[0.14em] text-steel transition-colors duration-300 hover:text-brand" data-testid="hire-back-home">
          <ArrowLeft className="h-4 w-4" /> VOLTAR AO SITE
        </a>
      </div>
    </main>
  );
}
